import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { AlertTriangle, Check, Copy, ExternalLink, Heart, Info, Phone, RotateCcw, Syringe } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { NeonCard } from "@/components/NeonCard";
import { PageSources } from "@/components/SourcesSheet";
import { listDangerousCombinations } from "@/lib/public-matrix.functions";
import { absoluteUrl, buildMeta, SITE_NAME, SITE_URL } from "@/lib/seo";
import { slugify } from "@/lib/slugify";
import { errorMessage } from "@/lib/error-capture";

const PAGE_DESCRIPTION =
  "Notfall-Kurzreferenz bei Mischkonsum und Überdosis: Notruf 112, stabile Seitenlage, Naloxon und die kritischsten Substanzkombinationen im Überblick.";

const STEPS = [
  {
    icon: <Phone size={20} />,
    title: "Notruf 112",
    text: "Rufe sofort 112 an. Sage der Leitstelle und dem Rettungsdienst ehrlich, welche Substanzen im Spiel sind. Zögere den Notruf nicht hinaus.",
  },
  {
    icon: <RotateCcw size={20} />,
    title: "Stabile Seitenlage",
    text: "Bewusstlos, aber normal atmend: in die stabile Seitenlage bringen, Mund frei halten, niemals allein lassen. Nichts essen oder trinken lassen.",
  },
  {
    icon: <Heart size={20} />,
    title: "Atmung kontrollieren",
    text: "Sprich die Person laut an und kontrolliere die Atmung. Wenn sie nicht normal atmet, beginne mit der Herzdruckmassage und lasse dir den Weg am Telefon ansagen.",
  },
  {
    icon: <Syringe size={20} />,
    title: "Naloxon bei Opioiden",
    text: "Verdacht auf eine Opioid-Überdosis (blasse oder bläuliche Haut, flache Atmung, kleine Pupillen): Naloxon verabreichen, wenn es verfügbar ist. Es wirkt nur bei Opioiden, schadet aber sonst nicht. Die Wirkung lässt nach, der Notruf bleibt Pflicht.",
  },
];

const LEGAL_NOTE =
  "Rechtliche Einordnung (Deutschland): § 323c StGB verpflichtet zu erforderlicher und zumutbarer Hilfe. Ein Drogennotruf begründet keine generelle Straffreiheit. Nach § 31a Abs. 1 Satz 1 BtMG kann die Staatsanwaltschaft bei bestimmten Betäubungsmitteldelikten von der Verfolgung absehen, wenn es um eine geringe Menge zum Eigenverbrauch geht, die Schuld gering wäre und kein öffentliches Interesse an der Strafverfolgung besteht.";

const LEGAL_SOURCES = [
  { title: "§ 323c StGB: Hilfeleistung", url: "https://www.gesetze-im-internet.de/stgb/__323c.html" },
  { title: "§ 31a BtMG: Absehen von der Verfolgung", url: "https://www.gesetze-im-internet.de/btmg_1981/__31a.html" },
];

function LegalNote() {
  return (
    <div style={{ marginTop: "1rem", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
      <p>{LEGAL_NOTE}</p>
      <p style={{ marginTop: "0.5rem" }}>
        Rechtsquellen:{" "}
        {LEGAL_SOURCES.map((source, i) => (
          <span key={source.url}>
            {i > 0 && "; "}
            <a href={source.url} style={{ color: "var(--accent)" }}>{source.title}</a>
          </span>
        ))}
      </p>
    </div>
  );
}

const PAGE_URL = absoluteUrl("/notfall");

/** WebPage plus HowTo, erzeugt aus denselben Schritten, die die Seite anzeigt. */
function buildStructuredData() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${PAGE_URL}#webpage`,
        url: PAGE_URL,
        name: `Notfall - ${SITE_NAME}`,
        description: PAGE_DESCRIPTION,
        inLanguage: "de-DE",
        publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      },
      {
        "@type": "HowTo",
        "@id": `${PAGE_URL}#howto`,
        name: "Erste Hilfe bei Mischkonsum und Überdosis",
        description: PAGE_DESCRIPTION,
        inLanguage: "de-DE",
        mainEntityOfPage: { "@id": `${PAGE_URL}#webpage` },
        step: STEPS.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.text,
          url: `${PAGE_URL}#notfall-schritte`,
        })),
      },
    ],
  };
}

export const Route = createFileRoute("/notfall")({
  validateSearch: (search: Record<string, unknown>): { embed?: 1 } =>
    String(search["embed"] ?? "") === "1" ? { embed: 1 } : {},
  head: ({ match }) => {
    const isEmbed = match.search.embed === 1;
    if (isEmbed) {
      return buildMeta({
        title: "Notfall-Kurzreferenz zum Einbetten",
        description: PAGE_DESCRIPTION,
        path: "/notfall",
        noindex: true,
      });
    }
    return {
      ...buildMeta({ title: "Notfall", description: PAGE_DESCRIPTION, path: "/notfall" }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildStructuredData()),
        },
      ],
    };
  },
  loader: () => listDangerousCombinations(),
  errorComponent: ({ error }) => (
    <div role="alert" style={{ padding: "2rem", color: "var(--text-secondary)" }}>
      Die Seite konnte nicht geladen werden. Bitte lade sie neu. ({errorMessage(error)})
    </div>
  ),
  notFoundComponent: () => <div style={{ padding: "2rem" }}>Diese Seite wurde nicht gefunden.</div>,
  component: NotfallRoute,
});

const RISK_LABEL: Record<string, string> = {
  dangerous: "Gefährlich",
  unsafe: "Hohes Risiko",
};

const MARKER_LABEL: Record<string, string> = {
  respiratory: "Atemdepression",
  serotonin: "Serotonin-Risiko",
  cardio: "Herz-Kreislauf",
  serotonin_cardio: "Serotonin und Herz-Kreislauf",
};

const LINK_SNIPPET = '<a href="https://restlicht.fyi/notfall">Notfall-Kurzreferenz bei Mischkonsum - Restlicht.FYI</a>';

const HTML_SNIPPET = [
  '<section>',
  '  <h2>Notfall bei Mischkonsum und Überdosis</h2>',
  '  <p><strong>Im Ernstfall zuerst den Notruf 112 wählen.</strong></p>',
  '  <ol>',
  ...STEPS.map((s) => `    <li><strong>${s.title}:</strong> ${s.text}</li>`),
  '  </ol>',
  `  <p>${LEGAL_NOTE}</p>`,
  `  <p>Rechtsquellen: ${LEGAL_SOURCES.map((source) => `<a href="${source.url}">${source.title}</a>`).join("; ")}</p>`,
  '  <p>Quelle und vollständige Kurzreferenz:',
  '    <a href="https://restlicht.fyi/notfall">Restlicht.FYI - Notfall-Kurzreferenz</a>',
  '  </p>',
  '</section>',
].join("\n");

const IFRAME_SNIPPET = [
  '<iframe',
  '  src="https://restlicht.fyi/notfall?embed=1"',
  '  title="Notfall-Kurzreferenz bei Mischkonsum - Restlicht.FYI"',
  '  width="100%"',
  '  height="900"',
  '  style="border:0;min-height:900px"',
  '  loading="lazy">',
  '</iframe>',
].join("\n");

const codeBoxStyle: React.CSSProperties = {
  display: "block",
  marginTop: "0.75rem",
  padding: "0.75rem 1rem",
  background: "var(--bg-primary)",
  border: "1px solid var(--accent-border)",
  borderRadius: "8px",
  color: "var(--text-primary)",
  fontSize: "0.85rem",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  userSelect: "all",
};

function CopyBlock({ label, code }: { label: string; code: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code kopiert.");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopieren hat nicht geklappt. Markiere den Code und kopiere ihn mit Strg und C.");
    }
  }

  return (
    <div style={{ marginTop: "1.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
        <strong style={{ color: "var(--text-primary)", fontSize: "0.9rem" }}>{label}</strong>
        <button
          type="button"
          onClick={copy}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            minHeight: "44px",
            padding: "0.4rem 0.9rem",
            borderRadius: "8px",
            background: "var(--accent-bg)",
            border: "1px solid var(--accent-border)",
            color: "var(--accent)",
            fontSize: "0.85rem",
            cursor: "pointer",
          }}
        >
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copied ? "Kopiert" : "Kopieren"}
        </button>
      </div>
      <code style={codeBoxStyle}>{code}</code>
    </div>
  );
}

function NotfallRoute() {
  const { embed } = Route.useSearch();
  return embed === 1 ? <NotfallEmbed /> : <NotfallPage />;
}

function NotfallEmbed() {
  const combinations = Route.useLoaderData();

  return (
    <div data-section="hr">
      <h1 style={{ fontSize: "1.35rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: "0.75rem" }}>
        Notfall-Kurzreferenz bei Mischkonsum
      </h1>
      <NeonCard style={{ padding: "0.9rem 1.1rem", marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
        <Phone size={16} style={{ color: "var(--accent)", flexShrink: 0 }} aria-hidden="true" />
        <span style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          Im Ernstfall zählt nur eines: 112 anrufen. Diese Übersicht ersetzt keine medizinische Hilfe.
        </span>
      </NeonCard>

      <ol style={{ display: "grid", gap: "0.6rem", listStyle: "none", margin: 0, padding: 0 }}>
        {STEPS.map((s, i) => (
          <li key={s.title}>
            <NeonCard style={{ padding: "0.9rem 1.1rem" }}>
              <h2 style={{ fontSize: "0.95rem", margin: 0, color: "var(--accent)" }}>
                {i + 1}. {s.title}
              </h2>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.88rem", marginTop: "0.4rem" }}>{s.text}</p>
            </NeonCard>
          </li>
        ))}
      </ol>
      <LegalNote />

      {combinations.length > 0 && (
        <section aria-labelledby="embed-kombis" style={{ marginTop: "1.5rem" }}>
          <h2 id="embed-kombis" style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: "0.6rem" }}>
            Kritische Kombinationen
          </h2>
          <ul style={{ display: "grid", gap: "0.35rem", listStyle: "none", margin: 0, padding: 0 }}>
            {combinations.map((c) => (
              <li key={`${c.a}-${c.b}`} style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>
                <strong style={{ color: "var(--text-primary)" }}>
                  {c.a} + {c.b}
                </strong>{" "}
                - {RISK_LABEL[c.risk_level] ?? c.risk_level}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p style={{ marginTop: "1.5rem", fontSize: "0.85rem" }}>
        <a href="https://restlicht.fyi/notfall" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
          Vollständige Kurzreferenz mit Quellen auf Restlicht.FYI
        </a>
      </p>
    </div>
  );
}

function NotfallPage() {
  const combinations = Route.useLoaderData();

  return (
    <div data-section="hr">
      <PageHeader icon={<AlertTriangle size={28} />} title="Notfall-Kurzreferenz" />

      <NeonCard
        style={{
          padding: "1rem 1.5rem",
          marginBottom: "2rem",
          display: "flex",
          alignItems: "center",
          gap: "0.6rem",
        }}
      >
        <Info size={16} style={{ color: "var(--accent)", flexShrink: 0 }} />
        <span style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
          Im Ernstfall zählt nur eines: 112 anrufen. Diese Seite ersetzt keine medizinische Hilfe, sondern hilft dir, die richtigen Schritte in der richtigen Reihenfolge zu gehen.
        </span>
      </NeonCard>

      <section aria-labelledby="notfall-schritte" style={{ marginBottom: "2.5rem" }}>
        <h2 id="notfall-schritte" style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: "1rem" }}>
          Was im Notfall tun
        </h2>
        <div className="posts-grid">
          {STEPS.map((s, i) => (
            <NeonCard as="article" key={s.title} style={{ padding: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", color: "var(--accent)", fontWeight: 700 }}>
                <span aria-hidden="true">{s.icon}</span>
                <h3 style={{ fontSize: "1rem", margin: 0 }}>
                  {i + 1}. {s.title}
                </h3>
              </div>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: "0.5rem" }}>{s.text}</p>
            </NeonCard>
          ))}
        </div>
        <LegalNote />
      </section>

      <section aria-labelledby="notfall-kombis" style={{ marginBottom: "2.5rem" }}>
        <h2 id="notfall-kombis" style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: "1rem" }}>
          Kritische Kombinationen im Überblick
        </h2>
        <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginBottom: "1rem" }}>
          Alle Kombinationen der Stufen "Gefährlich" und "Hohes Risiko" aus der{" "}
          <Link to="/matrix" style={{ color: "var(--accent)" }}>
            Interaktions-Matrix
          </Link>
          , alphabetisch und ohne Scrollen. Details und Quellen stehen in der Matrix.
        </p>
        {combinations.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
            Die Liste konnte nicht geladen werden. Die vollständige Übersicht findest du in der Interaktions-Matrix.
          </p>
        ) : (
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {combinations.map((c) => (
              <NeonCard key={`${c.a}-${c.b}`} style={{ padding: "0.75rem 1.25rem", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
                <strong style={{ color: "var(--text-primary)", fontSize: "0.95rem" }}>
                  {c.a} + {c.b}
                </strong>
                <span
                  className="mono-badge"
                  style={{
                    background: "var(--accent-bg)",
                    color: "var(--accent)",
                    border: "1px solid var(--accent-border)",
                    borderRadius: "20px",
                    padding: "0.15rem 0.6rem",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {RISK_LABEL[c.risk_level] ?? c.risk_level}
                </span>
                {c.markers.map((m) => (
                  <span key={m} style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                    {MARKER_LABEL[m] ?? m}
                  </span>
                ))}
                <Link
                  to="/matrix/$pair"
                  params={{ pair: [slugify(c.a), slugify(c.b)].sort().join("__") }}
                  style={{ color: "var(--accent)", fontSize: "0.85rem", marginLeft: "auto", textDecoration: "none" }}
                >
                  Details
                </Link>
              </NeonCard>
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="notfall-zitieren" style={{ marginBottom: "2.5rem" }}>
        <h2 id="notfall-zitieren" style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--text-primary)", marginBottom: "1rem" }}>
          Diese Seite zitieren oder einbetten
        </h2>
        <NeonCard style={{ padding: "1.25rem 1.5rem" }}>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
            Du betreibst eine Beratungsseite, einen Verein oder eine Linkliste? Diese Kurzreferenz darf ausdrücklich verlinkt und mit Quellenangabe zitiert werden. Fertiger Link zum Kopieren:
          </p>
          <CopyBlock label="Link zum Zitieren" code={LINK_SNIPPET} />
          <CopyBlock label="HTML-Block mit den vier Schritten" code={HTML_SNIPPET} />
          <CopyBlock label="Einbettung als iframe (immer aktuell)" code={IFRAME_SNIPPET} />
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginTop: "0.75rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <ExternalLink size={14} aria-hidden="true" />
            Fragen zur Einbettung: <a href="mailto:Contact@RestLicht.FYI" style={{ color: "var(--accent)" }}>Contact@RestLicht.FYI</a>
          </p>
        </NeonCard>
      </section>

      <PageSources path="/notfall" />
    </div>
  );
}
